<script setup lang="ts">
import usePoolStore from '~~/app/store/pool'
import useUserStore from '~~/app/store/user'
import useSimulationStore from '~~/app/store/simulation'
import useVehiclesStore from '~~/app/store/vehicles'
import { VehicleManagementView } from '~~/interfaces/vehicles'

const poolStore = usePoolStore()
const userStore = useUserStore()
const simulationStore = useSimulationStore()
const vehiclesStore = useVehiclesStore()

const S3_ASSETS_URL = useRuntimeConfig().public.S3_ASSETS_URL as string
const userId = useUserSession().user?.value?.id

const emits = defineEmits(['showSimulatorPanel', 'showVehicleSelection'])

const fetchPriceDetails = () => {
    if (!simulationStore.priceDetails || simulationStore.meta.isError) {
        simulationStore.fetchSimulationPriceDetails()
    }
}

const redirectTo = (path: string) => {
    if (isIframe()) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: path },
            document.referrer
        )
    } else {
        return navigateTo(getChargemapUrl(path), { external: true })
    }
}

const handleAddVehicle = () => {
    vehiclesStore.setVehicleManagementView(VehicleManagementView.FORM)
    emits('showVehicleSelection')
}

const handleModifyVehicle = () => {
    vehiclesStore.setVehicleManagementView(VehicleManagementView.LIST)
    emits('showVehicleSelection')
}

onMounted(() => {
    poolStore.fetchPrices(true)

    if (!simulationStore.priceDetails) {
        simulationStore.findVehicleForSimulation()
    }
})
</script>

<template>
    <div
        class="h-fit flex flex-col justify-start items-center gap-6 py-8 px-6 bg-white"
    >
        <h2 class="font-bold text-gray-l-700 text-2xl m-0">
            {{ $t('chargemap_prices') }}
        </h2>
        <DiscoverChargemapPass
            v-if="!userId || !userStore.userAlreadyHasAPass"
            class="w-full"
            :compatible="poolStore.canChargeWithChargemapPass"
            drawer
        />

        <ChargemapSkeletonLoader
            v-if="poolStore.pricesLoader"
            class="w-full h-[90px] rounded-2xl"
        />
        <!--                    eslint-disable-next-line-->
        <div v-else class="w-full" v-html="poolStore.prices" />

        <ChargemapSkeletonLoader
            v-if="simulationStore.meta.isLoading"
            class="w-full h-[160px] 425:h-[184px] rounded-2xl mt-9 425:mt-10"
        />
        <SimulationResultsDisplay
            v-else
            class="w-full"
            :price-details="simulationStore.priceDetails"
            :s3-url="S3_ASSETS_URL"
            :is-price-computable="!simulationStore.isPriceIncalculable"
            :is-vehicle-compatible="!simulationStore.hasNoCompatibleVehicle"
            :is-missing-vehicle="userStore.userVehicles.length === 0"
            :is-user-connected="!!userId"
            :is-boost="userStore.isBoostUser"
            :is-error="simulationStore.meta.isError"
            @seeDetails="$emit('showSimulatorPanel')"
            @retry="fetchPriceDetails()"
            @authenticateUser="redirectTo('/user/index')"
            @redirectToBoostLanding="redirectTo('mobile/boost-subscription')"
            @addVehicle="handleAddVehicle()"
            @modifyVehicle="handleModifyVehicle()"
        />
        <SimulationLegalBlock
            @redirectToConditions="redirectTo('about/legal')"
            @redirectToHelp="redirectTo('support')"
        />
    </div>
</template>
